// src/shoppingFlow/CheckoutPage.js
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';
import RequiredMark from '../components/RequiredMark';
import CardBrandIcon from '../components/CardBrandIcon';
import { formatCardNumber, detectCardBrand, cvcLengthForBrand, digitsOnly } from '../utils/cards';

const INPUT =
  "mt-1 w-full border border-gray-300 px-3 py-2 rounded-lg bg-white shadow-sm " +
  "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500";
const LABEL = "block text-sm font-medium text-gray-700";
const CARD  = "bg-white rounded-xl shadow p-6";

function fmt(amount, currency = 'CAD') {
  const n = Number(amount ?? 0);
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n); }
  catch { return `$${n.toFixed(2)}`; }
}

export default function CheckoutPage() {
  const navigate = useNavigate();

  // who am I?
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState({ items: [], totals: { subtotal: 0, currency: 'CAD', count: 0 } });
  const [loading, setLoading] = useState(true);

  // mode
  const isLoggedIn = !!user?.id;
  const [mode, setMode] = useState('guest'); // 'guest' | 'user'
  const [placing, setPlacing] = useState(false);

  // messages
  const [error, setError] = useState('');
  const [info, setInfo]   = useState('');

  // promo
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);

  // shipping/billing
  const [shipping, setShipping] = useState({
    name: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    region: '',
    postal: '',
    country: '',
  });
  const [billingSame, setBillingSame] = useState(true);
  const [billing, setBilling] = useState({
    name: '', address1: '', address2: '', city: '', region: '', postal: '', country: ''
  });

  // payment (fake)
  const [payment, setPayment] = useState({
    card_number: '',
    card_name: '',
    exp_month: '',
    exp_year: '',
    cvc: '',
  });

  // helper to load cart
  async function fetchCart() {
    const cartRes = await fetch(`${API_BASE}/api/cart`, { credentials: 'include' });
    const cartJson = cartRes.ok ? await cartRes.json() : { items: [], totals: { subtotal: 0, currency: 'CAD', count: 0 } };
    setCart(cartJson);
  }

  // load me + cart
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const meRes = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        const me = meRes.ok ? await meRes.json() : null;
        if (!alive) return;
        setUser(me);
        setMode(me?.id ? 'user' : 'guest');

        await fetchCart();

        // prefill from /api/users/me (richer profile) if logged in
        if (me?.id) {
          const profRes = await fetch(`${API_BASE}/api/users/me`, { credentials: 'include' });
          if (profRes.ok) {
            const p = await profRes.json();
            if (!alive) return;
            setShipping(s => ({
              ...s,
              name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
              email: p.email || s.email,
              address1: p.shipping_address1 || '',
              address2: p.shipping_address2 || '',
              city:     p.shipping_city || '',
              region:   p.shipping_region || '',
              postal:   p.shipping_postal_code || '',
              country:  p.shipping_country || '',
            }));
            setBillingSame(true);
          }
        }
      } catch {
        setError('Failed to load checkout data.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // derive totals (handles discount if present)
  const totals = useMemo(() => {
    const subtotal = Number(cart?.totals?.subtotal || 0);
    const discount = Number(
      cart?.totals?.discount ??
      cart?.totals?.discount_amount ??
      0
    );
    const currency = cart?.totals?.currency || 'CAD';
    const taxable = Math.max(0, subtotal - discount);
    const estTax  = +(taxable * 0.13).toFixed(2);
    const grand   = +(taxable + estTax).toFixed(2);
    return { subtotal, discount, taxable, estTax, grand, currency };
  }, [cart]);

  // promo info (if server returns these)
  const appliedCode =
    cart?.totals?.promo_code ||
    cart?.totals?.coupon_code ||
    cart?.coupon?.code || '';
  const appliedPercent =
    cart?.totals?.promo_percent ||
    cart?.totals?.discount_percent;

  // live brand + cvc max for current number
  const cardBrand = detectCardBrand(payment.card_number);
  const cvcMax    = cvcLengthForBrand(cardBrand);

  function onShipChange(e) {
    const { name, value } = e.target; setShipping(s => ({ ...s, [name]: value }));
  }
  function onBillChange(e) {
    const { name, value } = e.target; setBilling(b => ({ ...b, [name]: value }));
  }
  function onPayChange(e) {
    const { name, value } = e.target;

    if (name === 'card_number') {
      const { formatted } = formatCardNumber(value);
      setPayment(p => ({ ...p, card_number: formatted }));
      return;
    }
    if (name === 'cvc') {
      const brand = detectCardBrand(payment.card_number);
      const max = cvcLengthForBrand(brand);
      const clean = digitsOnly(value).slice(0, max);
      setPayment(p => ({ ...p, cvc: clean }));
      return;
    }
    if (name === 'exp_month' || name === 'exp_year') {
      const clean = digitsOnly(value).slice(0, 2);
      setPayment(p => ({ ...p, [name]: clean }));
      return;
    }

    setPayment(p => ({ ...p, [name]: value }));
  }

  async function applyPromo() {
    const code = promoInput.trim();
    if (!code) return;
    setApplyingPromo(true);
    setError(''); setInfo('');
    try {
      const res = await fetch(`${API_BASE}/api/cart/apply-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Invalid or expired promo code.');
      await fetchCart();
      setInfo(`Promo code "${code.toUpperCase()}" applied.`);
      setPromoInput('');
    } catch (e) {
      setError(e.message || 'Could not apply promo code.');
    } finally {
      setApplyingPromo(false);
    }
  }

  async function removePromo() {
    setApplyingPromo(true);
    setError(''); setInfo('');
    try {
      const res = await fetch(`${API_BASE}/api/cart/remove-coupon`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to remove promo.');
      await fetchCart();
      setInfo('Promo code removed.');
    } catch (e) {
      setError(e.message || 'Failed to remove promo code.');
    } finally {
      setApplyingPromo(false);
    }
  }

  function validate() {
    const must = (v) => String(v || '').trim().length > 0;
    const emailOk = /^\S+@\S+\.\S+$/.test(shipping.email || '');
    if (!must(shipping.name)) return 'Shipping name is required.';
    if (!emailOk) return 'Enter a valid email for shipping.';
    if (!must(shipping.address1) || !must(shipping.city) || !must(shipping.region) || !must(shipping.postal) || !must(shipping.country))
      return 'Complete the shipping address.';

    const bill = billingSame ? shipping : billing;
    if (!billingSame) {
      if (!must(bill.name) || !must(bill.address1) || !must(bill.city) || !must(bill.region) || !must(bill.postal) || !must(bill.country))
        return 'Complete the billing address.';
    }

    // payment (light)
    if (!/^\d{12,19}$/.test((payment.card_number || '').replace(/\s+/g, ''))) return 'Enter a valid card number.';
    if (!/^\d{2}$/.test(payment.exp_month)) return 'Invalid expiry month.';
    if (!/^\d{2}$/.test(payment.exp_year)) return 'Invalid expiry year.';
    if (!/^\d{3,4}$/.test(payment.cvc)) return 'Invalid CVC.';
    if (!payment.card_name.trim()) return 'Cardholder name is required.';
    return null;
  }

  async function placeOrder() {
    setError(''); setInfo('');
    if (!cart?.items?.length) { setError('Your cart is empty.'); return; }

    const v = validate();
    if (v) { setError(v); return; }

    setPlacing(true);
    try {
      // 1) validate card server-side (mock)
      const valRes = await fetch(`${API_BASE}/api/payments/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          card_number: payment.card_number,
          exp_month: payment.exp_month,
          exp_year: payment.exp_year,
          cvc: payment.cvc,
        }),
      });
      const val = await valRes.json();
      if (!valRes.ok || !val.ok) throw new Error(val.error || 'Payment validation failed.');

      // 2) checkout
      const bill = billingSame ? shipping : billing;
      const res = await fetch(`${API_BASE}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mode, shipping, billing: bill, payment: {
            card_name: payment.card_name,
            card_number: payment.card_number,
            exp_month: payment.exp_month,
            exp_year: payment.exp_year,
            cvc: payment.cvc,
          },
        }),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || 'Checkout failed.');
      navigate(`/order-confirmation/${out.order_id}`, { state: out });
    } catch (e) {
      setError(e.message || 'Checkout failed.');
    } finally {
      setPlacing(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading checkout…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-qa="checkout-page">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
            SodaSips
          </Link>
          <div className="text-sm text-gray-600">
            Cart: <strong>{cart?.totals?.count || 0}</strong> items
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alerts (always mounted) */}
        <QaAlert id="checkout-info"  dataQa="qa-checkout-info"  message={info}  variant="info" />
        <QaAlert id="checkout-error" dataQa="qa-checkout-error" message={error} variant="error" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: details */}
          <div className="lg:col-span-2 space-y-6">

            {/* Mode / Login hint */}
            <div className={CARD}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Checkout</h2>
                {!isLoggedIn ? (
                  <Link to="/signin" state={{ next: '/checkout' }} className="text-sm text-orange-600 hover:underline">
                    Have an account? Sign in
                  </Link>
                ) : null}
              </div>
              <div className="mt-3 flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mode"
                    value="guest"
                    checked={mode === 'guest'}
                    onChange={() => setMode('guest')}
                    className="text-orange-500"
                  />
                  <span>Checkout as Guest</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mode"
                    value="user"
                    checked={mode === 'user'}
                    onChange={() => setMode('user')}
                    className="text-orange-500"
                    disabled={!isLoggedIn}
                  />
                  <span>Use my account {isLoggedIn ? '(prefilled)' : '(sign in first)'}</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Fields marked <RequiredMark qa="checkout-legend-mark" /> are required.
              </p>
            </div>

            {/* Shipping */}
            <div className={CARD}>
              <h3 className="text-md font-semibold mb-3">Shipping Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-qa="checkout-shipping">
                <div>
                  <label className={LABEL} htmlFor="ship-name">Full Name <RequiredMark /></label>
                  <input id="ship-name" name="name" value={shipping.name} onChange={onShipChange} className={INPUT}
                         required aria-required="true" />
                </div>
                <div>
                  <label className={LABEL} htmlFor="ship-email">Email <RequiredMark /></label>
                  <input id="ship-email" name="email" value={shipping.email} onChange={onShipChange}
                         type="email" className={INPUT} required aria-required="true" />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL} htmlFor="ship-address1">Address Line 1 <RequiredMark /></label>
                  <input id="ship-address1" name="address1" value={shipping.address1} onChange={onShipChange}
                         className={INPUT} required aria-required="true" />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL} htmlFor="ship-address2">Address Line 2</label>
                  <input id="ship-address2" name="address2" value={shipping.address2} onChange={onShipChange} className={INPUT} />
                </div>
                <div>
                  <label className={LABEL} htmlFor="ship-city">City <RequiredMark /></label>
                  <input id="ship-city" name="city" value={shipping.city} onChange={onShipChange}
                         className={INPUT} required aria-required="true" />
                </div>
                <div>
                  <label className={LABEL} htmlFor="ship-region">Region <RequiredMark /></label>
                  <input id="ship-region" name="region" value={shipping.region} onChange={onShipChange}
                         className={INPUT} required aria-required="true" />
                </div>
                <div>
                  <label className={LABEL} htmlFor="ship-postal">Postal Code <RequiredMark /></label>
                  <input id="ship-postal" name="postal" value={shipping.postal} onChange={onShipChange}
                         className={INPUT} required aria-required="true" />
                </div>
                <div>
                  <label className={LABEL} htmlFor="ship-country">Country <RequiredMark /></label>
                  <input id="ship-country" name="country" value={shipping.country} onChange={onShipChange}
                         className={INPUT} required aria-required="true" />
                </div>
              </div>
            </div>

            {/* Billing */}
            <div className={CARD}>
              <div className="flex items-center justify-between">
                <h3 className="text-md font-semibold">Billing Address</h3>
                <label className="text-sm flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={billingSame}
                    onChange={(e) => setBillingSame(e.target.checked)}
                    className="text-orange-500"
                  />
                  Same as shipping
                </label>
              </div>
              {!billingSame && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3" data-qa="checkout-billing">
                  <div>
                    <label className={LABEL} htmlFor="bill-name">Full Name <RequiredMark /></label>
                    <input id="bill-name" name="name" value={billing.name} onChange={onBillChange}
                           className={INPUT} required aria-required="true" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={LABEL} htmlFor="bill-address1">Address Line 1 <RequiredMark /></label>
                    <input id="bill-address1" name="address1" value={billing.address1} onChange={onBillChange}
                           className={INPUT} required aria-required="true" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={LABEL} htmlFor="bill-address2">Address Line 2</label>
                    <input id="bill-address2" name="address2" value={billing.address2} onChange={onBillChange}
                           className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="bill-city">City <RequiredMark /></label>
                    <input id="bill-city" name="city" value={billing.city} onChange={onBillChange}
                           className={INPUT} required aria-required="true" />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="bill-region">Region <RequiredMark /></label>
                    <input id="bill-region" name="region" value={billing.region} onChange={onBillChange}
                           className={INPUT} required aria-required="true" />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="bill-postal">Postal Code <RequiredMark /></label>
                    <input id="bill-postal" name="postal" value={billing.postal} onChange={onBillChange}
                           className={INPUT} required aria-required="true" />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="bill-country">Country <RequiredMark /></label>
                    <input id="bill-country" name="country" value={billing.country} onChange={onBillChange}
                           className={INPUT} required aria-required="true" />
                  </div>
                </div>
              )}
            </div>

            {/* Payment (fake) */}
            <div className={CARD}>
              <h3 className="text-md font-semibold mb-3">Payment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-qa="checkout-payment">
                {/* Card number with live brand icon */}
                <div className="md:col-span-2">
                  <label className={LABEL} htmlFor="card-number">Card Number <RequiredMark /></label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <CardBrandIcon brand={cardBrand} />
                    </div>
                    <input
                      id="card-number"
                      name="card_number"
                      value={payment.card_number}
                      onChange={onPayChange}
                      inputMode="numeric"
                      autoComplete="cc-number"
                      className={`${INPUT} pl-14`}
                      placeholder="4111 1111 1111 1111"
                      required
                      aria-required="true"
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL} htmlFor="card-name">Cardholder Name <RequiredMark /></label>
                  <input
                    id="card-name"
                    name="card_name"
                    value={payment.card_name}
                    onChange={onPayChange}
                    className={INPUT}
                    autoComplete="cc-name"
                    required
                    aria-required="true"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={LABEL} htmlFor="exp-mm">MM <RequiredMark /></label>
                    <input
                      id="exp-mm"
                      name="exp_month"
                      value={payment.exp_month}
                      onChange={onPayChange}
                      inputMode="numeric"
                      autoComplete="cc-exp-month"
                      className={INPUT}
                      placeholder="MM"
                      required
                      aria-required="true"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="exp-yy">YY <RequiredMark /></label>
                    <input
                      id="exp-yy"
                      name="exp_year"
                      value={payment.exp_year}
                      onChange={onPayChange}
                      inputMode="numeric"
                      autoComplete="cc-exp-year"
                      className={INPUT}
                      placeholder="YY"
                      required
                      aria-required="true"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="cvc">CVC <RequiredMark /></label>
                    <input
                      id="cvc"
                      name="cvc"
                      value={payment.cvc}
                      onChange={onPayChange}
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      className={INPUT}
                      placeholder="CVC"
                      required
                      aria-required="true"
                      maxLength={cvcMax}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: summary + submit */}
          <aside className={CARD}>
            <h3 className="text-md font-semibold mb-4">Order Summary</h3>

            {/* Promo Code */}
            <div className="mb-4" data-qa="checkout-promo">
              <label className={LABEL} htmlFor="promo-input">Promo Code</label>
              <div className="mt-1 flex gap-2">
                <input
                  id="promo-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  className={INPUT}
                  placeholder="e.g., SAVE10"
                  data-qa="checkout-promo-input"
                />
                <button
                  type="button"
                  onClick={applyPromo}
                  disabled={applyingPromo || !promoInput.trim()}
                  className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white font-semibold disabled:opacity-60"
                  data-qa="checkout-apply-promo"
                >
                  {applyingPromo ? 'Applying…' : 'Apply'}
                </button>
              </div>

              {appliedCode && (
                <div className="mt-2 text-sm text-gray-700 flex items-center justify-between" data-qa="checkout-promo-applied">
                  <div>
                    Applied:&nbsp;
                    <span className="font-semibold">{appliedCode}</span>
                    {appliedPercent ? <span className="text-gray-500"> ({appliedPercent}%)</span> : null}
                  </div>
                  <button
                    type="button"
                    onClick={removePromo}
                    disabled={applyingPromo}
                    className="text-red-600 hover:underline"
                    data-qa="checkout-remove-promo"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{fmt(totals.subtotal, totals.currency)}</span>
              </div>

              {totals.discount > 0 && (
                <div className="flex items-center justify-between" data-qa="checkout-discount">
                  <span className="text-gray-600">Discount</span>
                  <span className="font-medium text-green-700">- {fmt(totals.discount, totals.currency)}</span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Estimated Tax</span>
                <span className="font-medium">{fmt(totals.estTax, totals.currency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">Free</span>
              </div>
              <div className="h-px bg-gray-100 my-3" />
              <div className="flex items-center justify-between text-base">
                <span className="font-semibold">Total</span>
                <span className="font-semibold" data-qa="checkout-grand-total">{fmt(totals.grand, totals.currency)}</span>
              </div>
            </div>

            <button
              onClick={placeOrder}
              disabled={placing || !cart?.items?.length}
              className="mt-5 w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-lg disabled:opacity-60 inline-flex items-center justify-center"
              data-qa="checkout-place-order"
            >
              {placing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Place Order
            </button>

            {!cart?.items?.length && (
              <p className="text-xs text-gray-500 mt-2">Add items to your cart to proceed.</p>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
